import {Component, inject} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {APP_MODAL_REF} from '../../../constants/modal/app-modal-ref.constant';
import {AppButtonTypeEnum} from '../../../enums/button/app-button-type.enum';
import {ButtonColorEnum} from '../../../enums/button/button-color.enum';
import {AppDialogModal} from '../../../models/modal/dialog/app-dialog-modal.model';
import {SystemInfo} from '../../../models/system-info.model';
import {ModalService} from '../../../services/modal/modal.service';
import {TranslationKey} from '../../../types/available-translations.type';
import {AppVersionNotesDialogData} from '../../../types/modal/dialog/app-version-notes-dialog-data.type';
import {ButtonComponent} from '../../buttons/button/button.component';

type VersionNote = {
    labelTranslationKey: TranslationKey;
    value: string;
};

@Component({
    selector: 'app-version-notes-dialog',
    templateUrl: './version-notes-dialog.component.html',
    styleUrl: './version-notes-dialog.component.css',
    imports: [
        ButtonComponent,
        TranslatePipe
    ]
})
export class VersionNotesDialogComponent {
    public readonly modalData: AppVersionNotesDialogData;
    protected readonly qaTag: string;
    protected readonly versionNotes: Array<VersionNote>;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly ButtonColorEnum = ButtonColorEnum;
    private readonly modalRef = inject<AppDialogModal<VersionNotesDialogComponent, AppVersionNotesDialogData, string>>(APP_MODAL_REF);
    private readonly modalService = inject(ModalService);
    private readonly translateService = inject(TranslateService);

    public constructor() {
        this.qaTag = this.modalRef.qaTag;
        this.modalData = this.modalRef.modalData;
        this.versionNotes = this.buildVersionNotes(this.modalData.systemInfo);
    }

    protected copy(): void {
        this.modalService.closeModal(this.modalRef, this.renderVersionNotes());
    }

    protected close(): void {
        this.modalService.closeModal(this.modalRef);
    }

    private buildVersionNotes(systemInfo: SystemInfo): Array<VersionNote> {
        const notFoundValue: string = this.translateService.instant('SHOW_VERSION_DIALOG.VALUE_NOT_FOUND');
        const optionalNotes: Array<[TranslationKey, string | null]> = [
            [
                'SHOW_VERSION_DIALOG.OS',
                systemInfo.os
            ],
            [
                'SHOW_VERSION_DIALOG.OS_VERSION',
                systemInfo.osVersion
            ],
            [
                'SHOW_VERSION_DIALOG.DEVICE',
                systemInfo.device
            ],
            [
                'SHOW_VERSION_DIALOG.BROWSER',
                systemInfo.browser
            ],
            [
                'SHOW_VERSION_DIALOG.BROWSER_VERSION',
                systemInfo.browserVersion
            ],
            [
                'SHOW_VERSION_DIALOG.APP_TYPE',
                systemInfo.appType
            ],
            [
                'SHOW_VERSION_DIALOG.SCREEN_RESOLUTION',
                systemInfo.screenResolution
            ]
        ];

        return [
            {
                labelTranslationKey: 'SHOW_VERSION_DIALOG.APP_VERSION_LABEL',
                value: systemInfo.appVersion ?? notFoundValue
            },
            {
                labelTranslationKey: 'SHOW_VERSION_DIALOG.API_VERSION_LABEL',
                value: systemInfo.apiVersion ?? notFoundValue
            },
            ...optionalNotes.flatMap(([labelTranslationKey, value]) => value ? [
                {
                    labelTranslationKey,
                    value
                }
            ] : [])
        ];
    }

    private renderVersionNotes(): string {
        return this.versionNotes
            .map(versionNote => this.translateService.instant(versionNote.labelTranslationKey) + ': ' + versionNote.value)
            .join(', ');
    }
}
